import {Injectable} from '@angular/core';
import {Observable, of, EMPTY, defer} from 'rxjs';
import {catchError, defaultIfEmpty, filter, map, switchMap, take} from 'rxjs/operators';
import {DienststellenService} from "../../core/services/apiservices/dienststellen.service";
import {AnliegenService} from "../../core/services/apiservices/anliegen.service";
import {DienststellenMap} from "../../core/model/dienststellen-map";
import {Dienststelle} from "../../core/model/dienststelle";
import {Anliegen} from "../../core/model/anliegen";
import {NachrichtenTypeEnum} from "../../core/model/nachricht/nachrichten-type.enum";
import {DienststellenArtEnum} from "../../core/model/dienststellen-art.enum";

@Injectable({providedIn: 'root'})
export class DienststellenAnliegenHelperService {

    constructor(
        private readonly dienststellenService: DienststellenService,
        private readonly anliegenService: AnliegenService
    ) {
    }

    /**
     * Liefert entweder { dienststelle, anliegen } oder null
     */
    public getDienststelleUndAnliegen(
        dstnr: string | null,
        dienststellenMap: DienststellenMap
    ): Observable<{ dienststelle: Dienststelle; anliegen: Anliegen[] } | null> {
        return this.resolveDienststelle$(dstnr, dienststellenMap).pipe(
            take(1),
            switchMap(dienststelle => {
                const typ = this.mapDienststellenArtToNachrichtenType(dienststelle.dienststellenArt);
                return this.anliegenService.getAnliegen(typ, dienststelle.dienststellenNummer).pipe(
                    take(1),
                    map(anliegen => ({dienststelle, anliegen}))
                );
            }),
            defaultIfEmpty(null),
            catchError(() => of(null))
        );
    }

    /**
     * Gibt genau eine Dienststelle oder nichts (EMPTY), wenn:
     * - dstnr fehlt
     * - nicht in DienststellenMap
     * - oder Endpunkt wenn dienststellenArt === GE
     */
    private resolveDienststelle$(
        dstnr: string | null,
        map: DienststellenMap
    ): Observable<Dienststelle> {
        return defer(() => {
            if (!dstnr || !this.isValidDstnr(dstnr)) return EMPTY;

            const ausMap = Object.values(map).find(d => d?.dienststellenNummer === dstnr);
            if (ausMap) {
               return of(ausMap);
            }

            return this.dienststellenService.getDienststelle(dstnr).pipe(
                take(1),
                filter((d): d is Dienststelle => d?.dienststellenArt === DienststellenArtEnum.GE),
                catchError(() => EMPTY)
            );
        });
    }

    private mapDienststellenArtToNachrichtenType(
        art: DienststellenArtEnum
    ): NachrichtenTypeEnum {
        switch (art) {
            case DienststellenArtEnum.AA:
                return NachrichtenTypeEnum.AA;
            case DienststellenArtEnum.GE:
            case DienststellenArtEnum.ZKT:
                return NachrichtenTypeEnum.JC;
            case DienststellenArtEnum.FAMKA:
                return NachrichtenTypeEnum.FAMKA;
            case DienststellenArtEnum.ZAV:
                return NachrichtenTypeEnum.ZAV;
            default:
                return null;
        }
    }

    private isValidDstnr(dstnr:string):boolean{
        //5-Ziffern (kann auch mit 0 beginnen)
        return /^\d{5}$/.test(dstnr);
    }

}